import React, { useState, useEffect, useCallback } from 'react';
import { GraphQLPMSService } from '../../services/graphqlPms';
import {
  ReviewCycle,
  ReviewCycleStatus,
  ReviewCycleType,
  ReviewPhase,
  REVIEW_CYCLE_TYPE_LABELS,
} from '../../types/pms';
// The lifecycle order — publish, then open, then complete. Stated once, in pmsWorkflow.
import {
  canCloseReview,
  canCompleteCycle,
  canOpenReview,
  canPublishCycle,
} from '../../services/pmsWorkflow';
import InlineLoader from '../common/InlineLoader';
import AppDialog, { AppDialogTone } from '../common/AppDialog';
import RightSidePanel from './RightSidePanel';
import './ReviewCycleConfig.css';

interface ReviewCycleForm {
  name: string;
  reviewCycleType: ReviewCycleType | '';
  startDate: string;
  endDate: string;
  submissionDeadline: string;
  midYearDeadline: string;
  endYearDeadline: string;
}

const EMPTY_FORM: ReviewCycleForm = {
  name: '',
  reviewCycleType: '',
  startDate: '',
  endDate: '',
  submissionDeadline: '',
  midYearDeadline: '',
  endYearDeadline: '',
};

/**
 * ReviewCycleConfig — manages review cycles under annual objective cycles.
 * HR Admin selects an annual cycle, then creates/edits/deletes/publishes review cycles.
 */
const ReviewCycleConfig: React.FC = () => {
  const [annualCycles, setAnnualCycles] = useState<ReviewCycle[]>([]);
  const [selectedAnnualId, setSelectedAnnualId] = useState('');
  const [reviewCycles, setReviewCycles] = useState<ReviewCycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ReviewCycleForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  /*
   * Confirmations use the app's own dialog rather than window.confirm.
   *
   * window.confirm renders browser chrome pinned to the top of the viewport, in the OS font,
   * with no relation to the page behind it — and it blocks the JS thread, so nothing can
   * repaint while it is open. AppDialog is the shared replacement (centred, themed, tone
   * icons, Escape to dismiss), already used elsewhere in the app.
   *
   * Wrapped in a promise so the five call sites keep reading top-to-bottom exactly as they did
   * with `if (!window.confirm(...)) return;` — the alternative is splitting each handler into a
   * "request" half and a "do it" callback, which scatters one decision across two places.
   */
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string;
    message: React.ReactNode;
    tone: AppDialogTone;
    confirmLabel: string;
    resolve: (confirmed: boolean) => void;
  } | null>(null);

  const confirmAction = useCallback(
    (opts: { title: string; message: React.ReactNode; tone: AppDialogTone; confirmLabel: string }) =>
      new Promise<boolean>(resolve => setConfirmDialog({ ...opts, resolve })),
    []
  );

  const settleConfirm = useCallback((confirmed: boolean) => {
    setConfirmDialog(prev => {
      prev?.resolve(confirmed);
      return null;
    });
  }, []);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let allCycles: ReviewCycle[] = [];
      try {
        allCycles = await GraphQLPMSService.getAllReviewCycles();
      } catch (err: any) {
        // Recover partial data from AppSync partial errors
        const partial = err?.data?.listPMSReviewCycles?.items;
        if (partial && Array.isArray(partial)) {
          allCycles = partial.filter((c: any) => c && c.id);
          console.warn('[ReviewCycleConfig] Partial recovery:', allCycles.length, 'cycles');
        } else {
          console.error('[ReviewCycleConfig] Load error:', err);
        }
      }

      // Annual cycles = explicitly no annualCycleId (parent cycles)
      // Only show ACTIVE annual cycles in the dropdown
      const withAnnualId = allCycles.filter((c: any) => c.annualCycleId);
      const parents = allCycles.filter((c: any) => !c.annualCycleId && c.status === ReviewCycleStatus.ACTIVE);
      
      // Children: cycles WITH annualCycleId (properly stored)
      const children = withAnnualId;

      console.groupCollapsed('[Review Cycle Configuration][Cycle Source] Validation');
      console.info('Source API', 'GraphQLPMSService.getAllReviewCycles -> listPMSReviewCycles (fully paginated)');
      console.info('Total review cycle records returned by API', allCycles.length);
      console.info('Annual parent cycles shown in annual selector', parents.length);
      console.info('HR-created child review cycles loaded', children.length);
      console.info('Applied filters', {
        annualSelectorStatusFilter: ReviewCycleStatus.ACTIVE,
        reviewCycleStatusFilter: 'None',
        reviewTypeFilter: 'None',
        hierarchyFilter: 'Child list requires annualCycleId',
        pagination: 'Handled by getAllReviewCycles',
        slicing: 'None',
      });
      console.table(children.map(cycle => ({
        id: cycle.id,
        name: cycle.name,
        reviewType: cycle.reviewCycleType || 'Unspecified',
        status: cycle.status,
        annualCycleId: cycle.annualCycleId,
      })));
      console.groupEnd();
      
      setAnnualCycles(parents);
      setReviewCycles(children);

      // Auto-select active annual cycle, or first one
      if (!selectedAnnualId || !parents.find(p => p.id === selectedAnnualId)) {
        const defaultId = parents[0]?.id || '';
        setSelectedAnnualId(defaultId);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedAnnualId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Re-filter when selectedAnnualId changes
  const filteredCycles = reviewCycles.filter(c => c.annualCycleId === selectedAnnualId);
  const selectedAnnual = annualCycles.find(a => a.id === selectedAnnualId);

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!form.name.trim()) errs.push('Review cycle name is required');
    if (!form.reviewCycleType) errs.push('Review type is required');
    if (!form.startDate) errs.push('Start date is required');
    if (!form.endDate) errs.push('End date is required');
    if (form.startDate && form.endDate && form.startDate >= form.endDate) errs.push('End date must be after start date');

    // Validate dates are within the selected annual cycle
    if (selectedAnnual && form.startDate && form.startDate < selectedAnnual.startDate) {
      errs.push(`Start date cannot be before annual cycle start (${formatDate(selectedAnnual.startDate)})`);
    }
    if (selectedAnnual && form.endDate && selectedAnnual.endDate && form.endDate > selectedAnnual.endDate) {
      errs.push(`End date cannot be after annual cycle end (${formatDate(selectedAnnual.endDate)})`);
    }

    return errs;
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setActionMessage({ type, text });
    setTimeout(() => setActionMessage(null), 4000);
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (errs.length > 0) { setErrors(errs); return; }
    if (!selectedAnnualId) { setErrors(['Please select an annual cycle first']); return; }
    setErrors([]);
    setSaving(true);
    try {
      if (editingId) {
        await GraphQLPMSService.updateReviewCycle(editingId, {
          name: form.name,
          reviewCycleType: form.reviewCycleType || undefined,
          startDate: form.startDate,
          endDate: form.endDate,
          submissionDeadline: form.submissionDeadline || undefined,
          midYearDeadline: form.midYearDeadline || undefined,
          endYearDeadline: form.endYearDeadline || undefined,
        });
        showMessage('success', 'Review cycle updated successfully');
      } else {
        await GraphQLPMSService.createReviewCycle({
          name: form.name,
          reviewCycleType: form.reviewCycleType || undefined,
          startDate: form.startDate,
          endDate: form.endDate,
          submissionDeadline: form.submissionDeadline || undefined,
          midYearDeadline: form.midYearDeadline || undefined,
          endYearDeadline: form.endYearDeadline || undefined,
          annualCycleId: selectedAnnualId,
        });
        showMessage('success', 'Review cycle created successfully');
      }
      setForm(EMPTY_FORM);
      setShowForm(false);
      setEditingId(null);
      await loadData();
    } catch (err) {
      setErrors(['Failed to save review cycle. Please try again.']);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (cycleId: string) => {
    if (!(await confirmAction({ title: 'Publish review cycle?', message: 'Employees and managers will see it in their dashboards and can start reviews for this period.', tone: 'info', confirmLabel: 'Publish' }))) return;
    setSaving(true);
    const result = await GraphQLPMSService.updateReviewCycle(cycleId, { status: ReviewCycleStatus.ACTIVE });
    if (result.success) {
      showMessage('success', 'Review cycle published to all employees');
    } else {
      showMessage('error', 'Failed to publish review cycle');
    }
    await loadData();
    setSaving(false);
  };

  const handleComplete = async (cycleId: string) => {
    if (!(await confirmAction({ title: 'Complete review cycle?', message: 'No further reviews can be submitted once this cycle is completed.', tone: 'warning', confirmLabel: 'Complete' }))) return;
    setSaving(true);
    await GraphQLPMSService.updateReviewCycle(cycleId, { status: ReviewCycleStatus.COMPLETED });
    showMessage('success', 'Review cycle marked as completed');
    await loadData();
    setSaving(false);
  };

  const handleOpenReview = async (cycleId: string) => {
    if (!(await confirmAction({ title: 'Open this review?', message: 'Employees will be able to submit their self-review. Once submitted, they cannot edit it.', tone: 'info', confirmLabel: 'Open Review' }))) return;
    setSaving(true);
    const result = await GraphQLPMSService.openReviewCycle(cycleId);
    showMessage(result.success ? 'success' : 'error', result.success ? 'Review opened' : (result.errors[0] || 'Failed to open review'));
    await loadData();
    setSaving(false);
  };

  const handleCloseReview = async (cycleId: string) => {
    if (!(await confirmAction({ title: 'Close this review?', message: 'Employees and managers will no longer be able to edit their ratings.', tone: 'warning', confirmLabel: 'Close Review' }))) return;
    setSaving(true);
    const result = await GraphQLPMSService.closeReviewCycle(cycleId);
    showMessage(result.success ? 'success' : 'error', result.success ? 'Review closed' : (result.errors[0] || 'Failed to close review'));
    await loadData();
    setSaving(false);
  };

  const handleDelete = async (cycleId: string, cycleName: string) => {
    if (!(await confirmAction({
      title: 'Delete review cycle?',
      message: `"${cycleName}" will be permanently removed. This action cannot be undone.`,
      tone: 'error',
      confirmLabel: 'Delete',
    }))) return;
    setSaving(true);
    const result = await GraphQLPMSService.deleteReviewCycle(cycleId);
    if (result.success) {
      showMessage('success', 'Review cycle deleted');
    } else {
      showMessage('error', result.errors[0] || 'Failed to delete review cycle');
    }
    await loadData();
    setSaving(false);
  };

  const handleEdit = (cycle: ReviewCycle) => {
    setForm({
      name: cycle.name,
      reviewCycleType: cycle.reviewCycleType || '',
      startDate: cycle.startDate,
      endDate: cycle.endDate,
      submissionDeadline: cycle.submissionDeadline || '',
      midYearDeadline: cycle.midYearDeadline || '',
      endYearDeadline: cycle.endYearDeadline || '',
    });
    setEditingId(cycle.id);
    setShowForm(true);
    setErrors([]);
  };

  const handleCancel = () => {
    setForm(EMPTY_FORM);
    setShowForm(false);
    setEditingId(null);
    setErrors([]);
  };

  const formatDate = (d: string) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  const getStatusClass = (status: ReviewCycleStatus): string => {
    switch (status) {
      case ReviewCycleStatus.ACTIVE: return 'pms-rc-status--active';
      case ReviewCycleStatus.COMPLETED: return 'pms-rc-status--completed';
      case ReviewCycleStatus.LOCKED: return 'pms-rc-status--locked';
      default: return 'pms-rc-status--draft';
    }
  };

  const getReviewPhaseClass = (phase?: ReviewPhase): string => {
    switch (phase) {
      case ReviewPhase.OPEN: return 'pms-rc-status--review-open';
      case ReviewPhase.CLOSED: return 'pms-rc-status--review-closed';
      default: return 'pms-rc-status--review-not-started';
    }
  };

  /**
   * The raw phase value, not a prettified one.
   *
   * HR drives the whole review workflow off this single field, so the screen shows exactly
   * what is stored — "Review Not Started" and NOT_STARTED reading differently is how you end
   * up unsure whether a review is genuinely unopened or merely displayed that way.
   */
  const getReviewPhaseLabel = (phase?: ReviewPhase): string => phase || ReviewPhase.NOT_STARTED;

  const getDeadlineStatus = (deadline: string): 'upcoming' | 'past' | 'far' => {
    if (!deadline) return 'far';
    const now = new Date();
    const dl = new Date(deadline + 'T00:00:00');
    const diff = dl.getTime() - now.getTime();
    const days = diff / (1000 * 60 * 60 * 24);
    if (days < 0) return 'past';
    if (days <= 7) return 'upcoming';
    return 'far';
  };

  if (loading) return <InlineLoader message="Loading review cycles" />;

  return (
    <div className="pms-rc-config">
      <div className="pms-rc-config__header">
        <div>
          <h1 className="pms-rc-config__title">Review Cycle Configuration</h1>
          <p className="pms-rc-config__subtitle">Create and manage review cycles under an annual objective cycle</p>
        </div>
        {selectedAnnualId && !showForm && selectedAnnual?.status !== ReviewCycleStatus.COMPLETED && (
          <button
            type="button"
            className="pms-rc-config__add-btn"
            onClick={() => { setShowForm(true); setEditingId(null); setForm(EMPTY_FORM); setErrors([]); }}
          >
            + New Review Cycle
          </button>
        )}
      </div>

      {/* Annual Cycle Selector */}
      <div className="pms-rc-annual-selector">
        <label className="pms-rc-annual-selector__label">Annual Objective Cycle</label>
        {annualCycles.length === 0 ? (
          <div className="pms-rc-annual-selector__empty">
            No annual cycles found. Create one from the <strong>Annual Objective Cycle</strong> page first.
          </div>
        ) : (
          <div className="pms-rc-annual-selector__row">
            <select
              className="pms-rc-annual-selector__select"
              value={selectedAnnualId}
              onChange={e => setSelectedAnnualId(e.target.value)}
            >
              {annualCycles.map(ac => (
                <option key={ac.id} value={ac.id}>
                  {ac.name} (Active)
                </option>
              ))}
            </select>
            {selectedAnnual && (
              <span className="pms-rc-annual-selector__info">
                {formatDate(selectedAnnual.startDate)} — {formatDate(selectedAnnual.endDate)}
              </span>
            )}
          </div>
        )}
      </div>

      {actionMessage && (
        <div className={`pms-rc-message pms-rc-message--${actionMessage.type}`} role="status">
          {actionMessage.type === 'success' ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
          )}
          {actionMessage.text}
        </div>
      )}

      {/* Only show form/list if an annual cycle is selected */}
      {selectedAnnualId && (
        <>
          {/* Form — Side Drawer */}
          {showForm && (
            <RightSidePanel
              isOpen={showForm}
              title={editingId ? 'Edit Review Cycle' : 'Create Review Cycle'}
              onClose={handleCancel}
              wide
            >
              <div className="pms-rc-form">
                {errors.length > 0 && (
                  <div className="pms-rc-form__errors" role="alert">
                    {errors.map((e, i) => <p key={i}>{e}</p>)}
                  </div>
                )}
                <div className="pms-rc-form__grid">
                  <div className="pms-rc-form__field pms-rc-form__field--full">
                    <label htmlFor="rc-name">Review Cycle Name</label>
                    <input id="rc-name" type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Q1 Review, Mid-Year Review, End-of-Year Review" />
                  </div>
                  <div className="pms-rc-form__field pms-rc-form__field--full">
                    <label htmlFor="rc-type">Review Type <span className="pms-rc-required">*</span></label>
                    <select
                      id="rc-type"
                      className="pms-rc-form__select"
                      value={form.reviewCycleType}
                      onChange={e => setForm(p => ({ ...p, reviewCycleType: e.target.value as ReviewCycleType }))}
                    >
                      <option value="">— Select Review Type —</option>
                      <option value={ReviewCycleType.YEAR_END}>{REVIEW_CYCLE_TYPE_LABELS[ReviewCycleType.YEAR_END]}</option>
                      <option value={ReviewCycleType.HIGH_LEVEL}>{REVIEW_CYCLE_TYPE_LABELS[ReviewCycleType.HIGH_LEVEL]}</option>
                    </select>
                  </div>
                  <div className="pms-rc-form__field">
                    <label htmlFor="rc-start">Start Date</label>
                    <input id="rc-start" type="date" value={form.startDate} onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))} min={selectedAnnual?.startDate || ''} max={selectedAnnual?.endDate || ''} />
                  </div>
                  <div className="pms-rc-form__field">
                    <label htmlFor="rc-end">End Date</label>
                    <input id="rc-end" type="date" value={form.endDate} onChange={e => setForm(p => ({ ...p, endDate: e.target.value }))} min={selectedAnnual?.startDate || ''} max={selectedAnnual?.endDate || ''} />
                  </div>
                  <div className="pms-rc-form__field">
                    <label htmlFor="rc-sub">Review Submission Deadline <span className="pms-rc-optional">(optional)</span></label>
                    <input id="rc-sub" type="date" value={form.submissionDeadline} onChange={e => setForm(p => ({ ...p, submissionDeadline: e.target.value }))} min={selectedAnnual?.startDate || ''} max={selectedAnnual?.endDate || ''} />
                  </div>
                  <div className="pms-rc-form__field">
                    <label htmlFor="rc-mid">Mid-Year Deadline <span className="pms-rc-optional">(optional)</span></label>
                    <input id="rc-mid" type="date" value={form.midYearDeadline} onChange={e => setForm(p => ({ ...p, midYearDeadline: e.target.value }))} min={selectedAnnual?.startDate || ''} max={selectedAnnual?.endDate || ''} />
                  </div>
                  <div className="pms-rc-form__field">
                    <label htmlFor="rc-eoy">End-of-Year Deadline <span className="pms-rc-optional">(optional)</span></label>
                    <input id="rc-eoy" type="date" value={form.endYearDeadline} onChange={e => setForm(p => ({ ...p, endYearDeadline: e.target.value }))} min={selectedAnnual?.startDate || ''} max={selectedAnnual?.endDate || ''} />
                  </div>
                </div>
                {selectedAnnual && (
                  <p className="pms-rc-form__hint">Dates must be within the annual cycle: {formatDate(selectedAnnual.startDate)} — {formatDate(selectedAnnual.endDate)}</p>
                )}
                <div className="pms-rc-form__actions">
                  <button className="pms-rc-form__cancel" onClick={handleCancel}>Cancel</button>
                  <button className="pms-rc-form__submit" onClick={handleSubmit} disabled={saving}>
                    {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Review Cycle'}
                  </button>
                </div>
              </div>
            </RightSidePanel>
          )}

          {/* Review Cycles List */}
          <div className="pms-rc-list">
            {filteredCycles.length === 0 ? (
              <div className="pms-rc-list__empty">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <p>No review cycles for this annual cycle yet</p>
                <span>Click "+ New Review Cycle" to create one</span>
              </div>
            ) : (
              filteredCycles.sort((a, b) => a.startDate.localeCompare(b.startDate)).map(cycle => (
                <div key={cycle.id} className={`pms-rc-card ${cycle.status === ReviewCycleStatus.ACTIVE ? 'pms-rc-card--active' : ''}`}>
                  <div className="pms-rc-card__header">
                    <div className="pms-rc-card__header-left">
                      <h3 className="pms-rc-card__name">{cycle.name}</h3>
                      {cycle.reviewCycleType && (
                        <span className="pms-rc-card__type">
                          {REVIEW_CYCLE_TYPE_LABELS[cycle.reviewCycleType] || cycle.reviewCycleType}
                        </span>
                      )}
                      <span className="pms-rc-card__period">{formatDate(cycle.startDate)} — {formatDate(cycle.endDate)}</span>
                    </div>
                    <span className={`pms-rc-status ${getStatusClass(cycle.status)}`}>
                      {cycle.status}
                    </span>
                    <span className="pms-rc-card__phase">
                      <span className="pms-rc-card__phase-caption">Review Phase</span>
                      <span className={`pms-rc-status ${getReviewPhaseClass(cycle.reviewPhase)}`}>
                        {getReviewPhaseLabel(cycle.reviewPhase)}
                      </span>
                    </span>
                  </div>
                  <div className="pms-rc-card__dates">
                    {cycle.submissionDeadline && (
                      <span className={getDeadlineStatus(cycle.submissionDeadline) === 'past' ? 'pms-rc-deadline--past' : getDeadlineStatus(cycle.submissionDeadline) === 'upcoming' ? 'pms-rc-deadline--upcoming' : ''}>
                        Submission: {formatDate(cycle.submissionDeadline)}
                        {getDeadlineStatus(cycle.submissionDeadline) === 'past' && ' (overdue)'}
                        {getDeadlineStatus(cycle.submissionDeadline) === 'upcoming' && ' (due soon)'}
                      </span>
                    )}
                    {cycle.midYearDeadline && (
                      <span className={getDeadlineStatus(cycle.midYearDeadline) === 'past' ? 'pms-rc-deadline--past' : getDeadlineStatus(cycle.midYearDeadline) === 'upcoming' ? 'pms-rc-deadline--upcoming' : ''}>
                        Mid-Year: {formatDate(cycle.midYearDeadline)}
                        {getDeadlineStatus(cycle.midYearDeadline) === 'past' && ' (overdue)'}
                        {getDeadlineStatus(cycle.midYearDeadline) === 'upcoming' && ' (due soon)'}
                      </span>
                    )}
                    {cycle.endYearDeadline && (
                      <span className={getDeadlineStatus(cycle.endYearDeadline) === 'past' ? 'pms-rc-deadline--past' : getDeadlineStatus(cycle.endYearDeadline) === 'upcoming' ? 'pms-rc-deadline--upcoming' : ''}>
                        End-of-Year: {formatDate(cycle.endYearDeadline)}
                        {getDeadlineStatus(cycle.endYearDeadline) === 'past' && ' (overdue)'}
                        {getDeadlineStatus(cycle.endYearDeadline) === 'upcoming' && ' (due soon)'}
                      </span>
                    )}
                  </div>
                  <div className="pms-rc-card__actions">
                    {/* Edit is always available (even after publishing) */}
                    {cycle.status !== ReviewCycleStatus.COMPLETED && (
                      <button className="pms-rc-card__btn pms-rc-card__btn--edit" onClick={() => handleEdit(cycle)}>
                        Edit
                      </button>
                    )}
                    {/*
                      * The lifecycle, in order, one step offered at a time.
                      *
                      * Publish and Open Review used to appear TOGETHER on a draft cycle: Open
                      * Review asked only `status !== COMPLETED && reviewPhase !== OPEN`, which
                      * a brand new cycle satisfies. HR saw two first steps with nothing to say
                      * which came first, and opening reviews on an unpublished cycle produces a
                      * cycle accepting self-reviews that no employee can see.
                      *
                      * The conditions live in pmsWorkflow so the rule is stated once, and they
                      * are rendered in lifecycle order so the row reads as a sequence:
                      *
                      *   Draft            Publish to Employees
                      *   Active           Open Review        → then Complete Cycle
                      *   Review open      Close Review       (Complete waits)
                      */}
                    {canPublishCycle(cycle) && (
                      <button
                        className="pms-rc-card__btn pms-rc-card__btn--activate"
                        onClick={() => handlePublish(cycle.id)}
                        disabled={saving}
                      >
                        Publish to Employees
                      </button>
                    )}
                    {canOpenReview(cycle) && (
                      <button className="pms-rc-card__btn pms-rc-card__btn--open-review" onClick={() => handleOpenReview(cycle.id)} disabled={saving}>
                        Open Review
                      </button>
                    )}
                    {canCloseReview(cycle) && (
                      <button className="pms-rc-card__btn pms-rc-card__btn--close-review" onClick={() => handleCloseReview(cycle.id)} disabled={saving}>
                        Close Review
                      </button>
                    )}
                    {canCompleteCycle(cycle) && (
                      <button className="pms-rc-card__btn pms-rc-card__btn--complete" onClick={() => handleComplete(cycle.id)} disabled={saving}>
                        Complete Cycle
                      </button>
                    )}
                    {/* Delete — available for Draft and Active */}
                    {cycle.status !== ReviewCycleStatus.COMPLETED && (
                      <button className="pms-rc-card__btn pms-rc-card__btn--delete" onClick={() => handleDelete(cycle.id, cycle.name)} disabled={saving}>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      <AppDialog
        open={!!confirmDialog}
        title={confirmDialog?.title ?? ''}
        message={confirmDialog?.message ?? ''}
        tone={confirmDialog?.tone ?? 'info'}
        confirmLabel={confirmDialog?.confirmLabel ?? 'OK'}
        cancelLabel="Cancel"
        onConfirm={() => settleConfirm(true)}
        onCancel={() => settleConfirm(false)}
      />
    </div>
  );
};

export default ReviewCycleConfig;
